import { FolderOpen } from '@tunasong/icons'
import type { NavigationEntityWithType } from '@tunasong/models'
import { getElementMedia, usePlugins } from '@tunasong/plugin-lib'
import type { FC } from 'react'
import { useNavigate } from 'react-router'
import { getEntityPath } from '../entity/entity-link-util.js'
import { IconButton, Menu, MenuContent, MenuItem, MenuTrigger } from '../primitives/index.js'
import styles from './breadcrumbs.module.css'

export interface BreadcrumbPathMenuProps {
  className?: string
  /** Root-first chain; the last entry is the current entity. */
  parentChain: NavigationEntityWithType[]
}

const entityName = (entity?: NavigationEntityWithType) => entity?.name || 'Untitled'

/**
 * Compact breadcrumb for narrow viewports: one "Show path" button that opens a menu of the
 * ancestors, root first, ending with the current entity (disabled, `aria-current="page"`).
 */
export const BreadcrumbPathMenu: FC<BreadcrumbPathMenuProps> = props => {
  const { className, parentChain } = props
  const plugins = usePlugins('all')
  const navigate = useNavigate()

  const lastIndex = parentChain.length - 1
  const current = parentChain[lastIndex]

  return (
    <nav className={[styles.nav, className].filter(Boolean).join(' ')} aria-label="breadcrumb">
      <Menu>
        <MenuTrigger
          render={
            <IconButton size="small" className={styles.pathButton} aria-label="Show path" title="Show path">
              <FolderOpen />
            </IconButton>
          }
        />
        <MenuContent aria-label={`Path to ${entityName(current)}`} aria-labelledby={undefined}>
          {parentChain.map((entity, idx) => {
            const isCurrent = idx === lastIndex
            const { colorCode, icon: Icon } = getElementMedia(entity.type, plugins)
            return (
              <MenuItem
                key={entity.id ?? idx}
                disabled={isCurrent}
                aria-current={isCurrent ? 'page' : undefined}
                onClick={() => navigate(getEntityPath({ entity }))}
                className={styles.pathItem}
              >
                <Icon size={20} className={styles.iconWrapper} style={{ color: colorCode }} />
                {entityName(entity)}
              </MenuItem>
            )
          })}
        </MenuContent>
      </Menu>
    </nav>
  )
}
